// frontend/src/features/livestock/CompareButton.tsx
// Compare, as Tank Shots and the organism page show it (livestock compare plan
// 5.3, decision 18): always shown; with fewer than two photos it is disabled
// and says why, in a tooltip that also shows on focus and tap.

import type { MouseEvent } from 'react';
import { Button, Tooltip } from '@mantine/core';
import { IconLayoutColumns } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { compareReason } from './compareShots';
import { HEADER_BUTTON_HEIGHT } from './constants';
import { TANK_WORDS, type SubjectWords } from './photoSubject';

export interface CompareButtonProps {
  /** How many photos there are to compare. */
  total: number;
  /** For the reason it is disabled; tank shot words by default. */
  words?: SubjectWords;
  /** Opens the compare page (a link), or ... */
  to?: string;
  /** ... starts picking photos (Tank Shots' select mode). */
  onSelect?: () => void;
  /** Picking is under way: the bar at the foot has Compare then. */
  selecting?: boolean;
}

export function CompareButton({
  total,
  words = TANK_WORDS,
  to,
  onSelect,
  selecting = false,
}: CompareButtonProps): JSX.Element {
  const reason = compareReason(total, words);
  const icon = <IconLayoutColumns size={16} />;
  if (!reason) {
    if (to !== undefined) {
      return (
        <Button
          component={Link}
          to={to}
          variant="light"
          leftSection={icon}
          h={HEADER_BUTTON_HEIGHT}
        >
          Compare
        </Button>
      );
    }
    return (
      <Button
        variant="light"
        leftSection={icon}
        h={HEADER_BUTTON_HEIGHT}
        disabled={selecting}
        onClick={onSelect}
      >
        Compare
      </Button>
    );
  }
  // data-disabled rather than disabled, so the tooltip still shows on hover, focus and tap.
  return (
    <Tooltip label={reason} events={{ hover: true, focus: true, touch: true }}>
      <Button
        data-disabled
        aria-disabled
        onClick={(event: MouseEvent) => event.preventDefault()}
        variant="light"
        leftSection={icon}
        h={HEADER_BUTTON_HEIGHT}
      >
        Compare
      </Button>
    </Tooltip>
  );
}
